import { Delete as DeleteIcon } from '@mui/icons-material'
import { Button } from '@mui/material'
import { FC, MouseEvent, useCallback, useState } from 'react'
import { Confirm, useDelete, useNotify, useRecordContext, useRedirect, useRefresh, useTranslate } from 'react-admin'

import type { ActiveInspection, InspectionEvidenceCounts } from '../../models/activeInspection'

type Translate = ReturnType<typeof useTranslate>

/**
 * Renders the evidence an inspection carries as "3 fotos y 1 precinto".
 *
 * Singular and plural come from separate keys rather than polyglot's smart_count
 * so every leaf stays a typed string in the `Translations` interface.
 */
const evidenceSummary = (counts: InspectionEvidenceCounts, translate: Translate): string => {
  const parts: string[] = []

  if (counts.photos > 0) {
    parts.push(
      translate(counts.photos === 1 ? 'pages.inspections.delete.photo_one' : 'pages.inspections.delete.photo_many', {
        count: counts.photos,
      }),
    )
  }
  if (counts.seals > 0) {
    parts.push(
      translate(counts.seals === 1 ? 'pages.inspections.delete.seal_one' : 'pages.inspections.delete.seal_many', { count: counts.seals }),
    )
  }

  return parts.join(translate('pages.inspections.delete.join'))
}

export interface DeleteInspectionButtonProps {
  /** Explicit record; falls back to the React Admin record context. */
  record?: ActiveInspection
  /**
   * Marks the detail-page usage. When true, the button reads "Eliminar
   * inspección" and returns to the Operativa list once the row is gone;
   * otherwise it reads "Eliminar" and refreshes the current view in place.
   */
  redirectOnSuccess?: boolean
}

/**
 * Hard-deletes a manual inspection, the same way the mobile app does: a row
 * DELETE against `inspections`, which cascades into `inspection_photos` and
 * `inspection_seals` and — through their AFTER DELETE triggers — into the
 * storage bucket. Nothing is recoverable afterwards, so the confirmation names
 * what is about to be destroyed.
 *
 * This is NOT `SoftDeleteButton`: that one flips `is_deleted` on the archived
 * `historical_inspections` copy and destroys nothing.
 *
 * Rendered for every inspection the app created (`is_local`), including one
 * SOSTRAT has since seen and whose hand marker is gone. The database enforces the
 * same rule — the RLS delete policy on `public.inspections` is
 * `USING (is_local = true)` — so a SOSTRAT row is unreachable from here even if
 * this gate were bypassed.
 */
export const DeleteInspectionButton: FC<DeleteInspectionButtonProps> = ({ record: recordProp, redirectOnSuccess = false }) => {
  const contextRecord = useRecordContext<ActiveInspection>()
  const record = recordProp ?? contextRecord
  const translate = useTranslate()
  const notify = useNotify()
  const refresh = useRefresh()
  const redirect = useRedirect()
  const [open, setOpen] = useState(false)
  const [deleteOne, { isPending }] = useDelete()

  const handleOpen = useCallback((event: MouseEvent<HTMLButtonElement>) => {
    // Keep the click off any surrounding element with its own click behaviour.
    event.stopPropagation()
    setOpen(true)
  }, [])

  const handleClose = useCallback(() => {
    setOpen(false)
  }, [])

  const handleConfirm = useCallback(() => {
    if (!record) return
    void deleteOne(
      'inspections',
      { id: record.id, previousData: record },
      {
        // Pessimistic (the useDelete default): the row must be gone server-side
        // before the list is refreshed. An undoable delete would hide a row the
        // database may still hold.
        onSuccess: () => {
          notify('pages.inspections.delete.success', { type: 'info' })
          setOpen(false)
          if (redirectOnSuccess) {
            redirect('list', 'inspections')
          } else {
            refresh()
          }
        },
        onError: () => {
          notify('pages.inspections.delete.error', { type: 'error' })
          setOpen(false)
        },
      },
    )
  }, [record, deleteOne, notify, refresh, redirect, redirectOnSuccess])

  if (!record?.isLocal) return null

  // The detail page shows the button in a header action stack, away from the
  // row that identifies the inspection, so it spells the subject out.
  const label = translate(redirectOnSuccess ? 'pages.inspections.delete.detail_label' : 'pages.inspections.delete.label')

  // A just-created inspection owns nothing but its container photo, so spelling
  // out "1 foto" would put a loss warning on a delete that costs nothing. The
  // enriched copy appears only once there is real inspection work to lose.
  const hasEvidence = record.evidenceCounts.hasCapturedEvidence

  return (
    <>
      <Button color="error" variant="text" size="small" onClick={handleOpen} startIcon={<DeleteIcon />} aria-label={label}>
        {label}
      </Button>
      <Confirm
        isOpen={open}
        loading={isPending}
        title="pages.inspections.delete.confirm_title"
        content={hasEvidence ? 'pages.inspections.delete.confirm_content_with_evidence' : 'pages.inspections.delete.confirm_content'}
        translateOptions={hasEvidence ? { evidence: evidenceSummary(record.evidenceCounts, translate) } : undefined}
        onConfirm={handleConfirm}
        onClose={handleClose}
      />
    </>
  )
}
